// Pure helpers for the Jaberi revenue-posting edit flow (no React, no API calls).

/**
 * @typedef {Object} RevenuePostingRow  a row of GET /GetPostings
 * @property {string|number} postingNo
 * @property {string} voucherNo
 * @property {string} financialYear
 * @property {string} periodFrom
 * @property {string} periodTo
 * @property {string} postingDate
 * @property {string} debitAcCode
 * @property {string} debitAcName
 * @property {string} creditAcCode
 * @property {string} creditAcName
 * @property {number} lineCount
 * @property {number} totalAmount
 * @property {string} commonNarration
 * @property {string} modifiedBy
 * @property {string} modifiedOn
 * @property {boolean} isApproved
 * @property {boolean} canEdit
 * @property {number} totalRecords
 */

/**
 * @typedef {Object} RevenuePostingHeader  `header` of GET /GetPostingByNo/{postingNo}
 * @property {string|number} postingNo
 * @property {string} periodFrom
 * @property {string} periodTo
 * @property {string} postingDate
 * @property {string} debitAcCode
 * @property {string} debitAcName
 * @property {string} creditAcCode
 * @property {string} creditAcName
 * @property {string} voucherNo
 * @property {string} financialYear
 * @property {string} commonNarration
 * @property {boolean} isApproved
 * @property {boolean} canEdit
 * @property {string} editBlockReason
 * @property {string} modifiedBy
 * @property {string} modifiedOn
 */

export const MSG_NO_POSTING_DATE = 'Enter Posting Date.';
export const MSG_INVALID_AMOUNT = 'Invalid Amount Found.';
export const MSG_MISSING_RECORD_ID = 'Every line must carry its RecordId when editing a posting.';
export const MSG_NO_IMPLEMENTATION = 'No Implementation For this User Company';
export const MSG_ALREADY_POSTED = 'Some units are already posted for this period.';

/** The server decides; never derive this from isApproved. */
export const canEditPosting = (row) => row?.canEdit === true;

/** isPosted is a number, not a boolean. */
export const hasPostedLine = (lines) => (lines || []).some((l) => l?.isSelected && Number(l?.isPosted) > 0);

const hasRecordId = (line) => Number(line?.recordId) > 0;

/**
 * Checks the edited lines before a save (rules 4.1.1 - 4.1.4).
 * @returns {{ valid: boolean, message?: string, emptyPosting?: boolean }}
 */
export const validateUpdate = (postingDate, lines) => {
    if (!postingDate) return { valid: false, message: MSG_NO_POSTING_DATE };
    const all = lines || [];
    const ticked = all.filter((l) => l?.isSelected);
    if (ticked.some((l) => !(Number(l?.postingAmount) > 0))) return { valid: false, message: MSG_INVALID_AMOUNT };
    if (all.some((l) => !hasRecordId(l))) return { valid: false, message: MSG_MISSING_RECORD_ID };
    if (ticked.length === 0) return { valid: false, emptyPosting: true };
    return { valid: true };
};

/**
 * Body of PUT /UpdateLeaseRevenuePosting/{postingNo} (same shape as PostLeaseRevenue).
 * EVERY line is sent, ticked or not: an unticked line that has a recordId is how the server
 * is told to remove it from the posting.
 * @param {{ periodFrom: string, periodTo: string, postingDate: string, debitAccount: string, creditAccount: string, narration: string }} header
 * @param {Array<object>} lines  grid rows, with isSelected set to the current tick state
 */
export const toUpdatePayload = (header, lines) => ({
    leaseRevenueMain: {
        periodFrom: header?.periodFrom,
        periodTo: header?.periodTo,
        postingDate: header?.postingDate,
        debitAccount: header?.debitAccount,
        creditAccount: header?.creditAccount,
        narration: header?.narration || '',
        _LeaseRevenuGrid: (lines || []).map((l) => ({ ...l, isSelected: !!l?.isSelected })),
    },
});
